/**
 * InputRoundSubmit — Participant submission page for a single input round.
 *
 * - Shows all items + attributes in scope for the round.
 * - Per-attribute: pill selector + confidence slider + optional rationale.
 * - In blind rounds: other participants' inputs are hidden.
 * - In open rounds: existing estimates are shown below each attribute.
 * - Submit button marks the participant as "submitted" and navigates back.
 * - Facilitators can add items to the round directly from here.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Users2,
  CheckCircle2,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronUp,
  Loader2,
  Plus,
  CalendarDays,
} from 'lucide-react';
import { useInputRoundsStore } from '../../../stores/prioritization/inputRoundsStore';
import { useValueProposalStore } from '../../../stores/prioritization/valueProposalStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useAttributesStore } from '../../../stores/prioritization/attributesStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import type { MeasurementScale } from '../../../types/prioritization/lifecycle';

// ============================================================================
// Attribute input card (one per round item)
// ============================================================================

interface AttributeInputCardProps {
  roundId: string;
  itemId: string;
  attributeKey: string;
  attributeLabel: string;
  scale: MeasurementScale | null;
  scaleId: string | null;
  showOthers: boolean;
  userId: string;
  userName: string;
  currentRole: string;
  onSubmitted: () => void;
}

function AttributeInputCard({
  roundId,
  itemId,
  attributeKey,
  attributeLabel,
  scale,
  scaleId,
  showOthers,
  userId,
  userName,
  currentRole,
  onSubmitted,
}: AttributeInputCardProps) {
  const proposals = useValueProposalStore((s) => s.proposals);
  const proposeValue = useValueProposalStore((s) => s.proposeValue);
  const fetchProposals = useValueProposalStore((s) => s.fetchFromSupabase);

  const [selectedValue, setSelectedValue] = useState<string | null>(null);
  const [confidence, setConfidence] = useState(80);
  const [rationale, setRationale] = useState('');
  const [showRationale, setShowRationale] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // My prior submission for this (item, attribute, round)
  const myPrior = useMemo(
    () =>
      proposals.find(
        (p) =>
          p.itemId === itemId &&
          p.attributeKey === attributeKey &&
          p.roundId === roundId &&
          p.proposedBy === userId &&
          p.status === 'proposed'
      ),
    [proposals, itemId, attributeKey, roundId, userId]
  );

  // Others' submissions (for open rounds)
  const othersProposals = useMemo(
    () =>
      proposals.filter(
        (p) =>
          p.itemId === itemId &&
          p.attributeKey === attributeKey &&
          p.roundId === roundId &&
          p.proposedBy !== userId &&
          p.status === 'proposed'
      ),
    [proposals, itemId, attributeKey, roundId, userId]
  );

  // Pre-populate with prior submission
  useEffect(() => {
    if (myPrior) {
      setSelectedValue(String(myPrior.proposedValue));
      if (myPrior.confidence != null) setConfidence(Math.round(myPrior.confidence * 100));
      if (myPrior.rationale) { setRationale(myPrior.rationale); setShowRationale(true); }
      setSubmitted(true);
    }
  }, [myPrior]);

  const points = scale?.points ?? [];

  const handleSubmit = useCallback(async () => {
    if (!selectedValue) return;
    setSubmitting(true);
    setError(null);
    try {
      await proposeValue({
        itemId,
        attributeKey,
        scaleId: scaleId ?? scale?.id ?? null,
        proposedValue: selectedValue,
        proposedBy: userId,
        proposedByName: userName,
        proposerRole: currentRole,
        confidence: confidence / 100,
        rationale: rationale.trim() || undefined,
        valueKind: 'estimate',
        roundId,
      });
      await fetchProposals();
      setSubmitted(true);
      onSubmitted();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  }, [selectedValue, confidence, rationale, itemId, attributeKey, userId, userName, currentRole, roundId, proposeValue, fetchProposals, onSubmitted]);

  return (
    <div className={` border p-4 space-y-3 transition-colors ${submitted ? 'border-[var(--green)] bg-[var(--green-dim)]/30' : 'border-[var(--rule)] bg-[var(--bg-raised)]'}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <span className="text-[12px] font-semibold uppercase tracking-wide text-[var(--text-ter)]">
          {attributeLabel}
        </span>
        {submitted && (
          <span className="flex items-center gap-1 text-[11px] text-[var(--green)] dark:text-[var(--text-ter)] font-medium">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Submitted
          </span>
        )}
      </div>

      {/* Pills */}
      {points.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {points.map((pt) => {
            const isSelected = selectedValue === pt.label;
            return (
              <button
                key={pt.value}
                type="button"
                onClick={() => { setSelectedValue(pt.label); setSubmitted(false); }}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                  isSelected
                    ? 'bg-[var(--slate)] border-[var(--slate)] text-white'
                    : 'bg-[var(--bg-raised)] border-[var(--rule)] text-[var(--text-sec)] hover:border-[var(--slate)] hover:bg-[var(--slate-dim)]'
                }`}
                style={{ borderWidth: '0.5px' }}
              >
                {pt.label}
              </button>
            );
          })}
        </div>
      ) : (
        <input
          type="text"
          value={selectedValue ?? ''}
          onChange={(e) => { setSelectedValue(e.target.value); setSubmitted(false); }}
          placeholder="Enter your estimate…"
          className="w-full px-3 py-2 border border-[var(--rule)] text-sm focus:ring-2 focus:ring-violet-500 focus:border-[var(--slate)]"
        />
      )}

      {/* Confidence */}
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <label className="text-[11px] font-medium text-[var(--text-ter)]">Confidence</label>
          <span className="text-[11px] font-semibold text-[var(--text-ter)]">{confidence}%</span>
        </div>
        <input
          type="range"
          min={10}
          max={100}
          step={10}
          value={confidence}
          onChange={(e) => { setConfidence(Number(e.target.value)); setSubmitted(false); }}
          className="w-full h-1.5 rounded-full appearance-none cursor-pointer accent-violet-600"
        />
      </div>

      {/* Rationale toggle */}
      <button
        type="button"
        onClick={() => setShowRationale((v) => !v)}
        className="text-[11px] text-[var(--text-ter)] hover:text-[var(--text-ter)] flex items-center gap-1"
      >
        {showRationale ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        {rationale ? 'Edit rationale' : 'Add rationale (optional)'}
      </button>
      {showRationale && (
        <textarea
          value={rationale}
          onChange={(e) => { setRationale(e.target.value); setSubmitted(false); }}
          placeholder="Why do you hold this estimate?"
          rows={2}
          className="w-full text-xs border border-[var(--rule)] px-2.5 py-1.5 bg-[var(--bg-raised)] focus:outline-none focus:ring-1 focus:ring-violet-400 resize-none"
        />
      )}

      {/* Submit button */}
      {selectedValue && !submitted && (
        <button
          type="button"
          disabled={submitting}
          onClick={handleSubmit}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--slate)] text-white text-xs font-medium hover:bg-[var(--slate)] disabled:opacity-50"
        >
          {submitting ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
          Submit estimate
        </button>
      )}

      {error && <p className="text-xs text-[var(--coral)]">{error}</p>}

      {/* Others' inputs (open mode only) */}
      {showOthers && othersProposals.length > 0 && (
        <div className="pt-2 border-t border-[var(--rule)] space-y-1">
          <p className="text-[10px] font-medium text-[var(--text-ter)] uppercase tracking-wide">Other estimates</p>
          {othersProposals.map((p) => (
            <div key={p.id} className="flex items-center gap-2 text-xs text-[var(--text-sec)]">
              <span className="font-medium">{String(p.proposedValue)}</span>
              <span className="text-[var(--text-ter)]">{p.proposedByName || p.proposedBy.slice(0, 8)}</span>
              {p.confidence != null && (
                <span className="text-[var(--text-ter)]">{Math.round(p.confidence * 100)}% conf.</span>
              )}
              {p.rationale && (
                <span className="text-[var(--text-ter)] italic truncate">"{p.rationale}"</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Add items to round (facilitator-only, accessible from submit page)
// ============================================================================

function AddItemsForm({ roundId, onDone }: { roundId: string; onDone: () => void }) {
  const items = useItemsStore((s) => s.items);
  const attributes = useAttributesStore((s) => s.attributes);
  // Subscribe to the raw array, not the derived fn, to avoid creating a new
  // reference on every render (which causes a Zustand selector infinite loop).
  const allRoundItems = useInputRoundsStore((s) => s.roundItems);
  const existingItems = useMemo(
    () => allRoundItems.filter((i) => i.roundId === roundId),
    [allRoundItems, roundId]
  );
  const addItemsToRound = useInputRoundsStore((s) => s.addItemsToRound);

  const [selectedItemId, setSelectedItemId] = useState('');
  const [selectedAttrKey, setSelectedAttrKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const alreadyScoped = new Set(existingItems.map((ri) => `${ri.itemId}::${ri.attributeKey}`));
  const eligibleItems = items.filter((i) => i.lifecycle_stage !== 'intake');
  const requiredAttrs = attributes.filter((a) => a.required);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedItemId || !selectedAttrKey) return;
    if (alreadyScoped.has(`${selectedItemId}::${selectedAttrKey}`)) {
      setError('Already in scope');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await addItemsToRound(roundId, [{ itemId: selectedItemId, attributeKey: selectedAttrKey }]);
      setSelectedItemId('');
      setSelectedAttrKey('');
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleAdd} className="flex flex-wrap gap-2 items-end mt-2">
      <select
        value={selectedItemId}
        onChange={(e) => setSelectedItemId(e.target.value)}
        className="flex-1 min-w-[160px] px-3 py-2 border border-[var(--rule)] text-sm focus:ring-2 focus:ring-violet-500"
      >
        <option value="">Select item…</option>
        {eligibleItems.map((i) => <option key={i.id} value={i.id}>{i.title}</option>)}
      </select>
      <select
        value={selectedAttrKey}
        onChange={(e) => setSelectedAttrKey(e.target.value)}
        className="flex-1 min-w-[140px] px-3 py-2 border border-[var(--rule)] text-sm focus:ring-2 focus:ring-violet-500"
      >
        <option value="">Select attribute…</option>
        {requiredAttrs.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
      </select>
      <button
        type="submit"
        disabled={loading || !selectedItemId || !selectedAttrKey}
        className="flex items-center gap-1 px-3 py-2 bg-[var(--slate)] text-white text-sm font-medium hover:bg-[var(--slate)] disabled:opacity-50"
      >
        <Plus className="h-4 w-4" />
        Add
      </button>
      {error && <p className="text-xs text-[var(--coral)] self-center">{error}</p>}
    </form>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function InputRoundSubmit() {
  const { roundId } = useParams<{ roundId: string }>();
  const navigate = useNavigate();

  const {
    rounds,
    fetchFromSupabase: fetchRounds,
    getItemsForRound,
    markParticipantSubmitted,
  } = useInputRoundsStore();

  const proposals = useValueProposalStore((s) => s.proposals);
  const fetchProposals = useValueProposalStore((s) => s.fetchFromSupabase);
  const items = useItemsStore((s) => s.items);
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);
  const attributes = useAttributesStore((s) => s.attributes);
  const fetchAttributes = useAttributesStore((s) => s.fetchFromSupabase);
  const userId = useUserStore((s) => s.userId);
  const userName = useUserStore((s) => s.userName);
  const currentRole = useUserStore((s) => s.currentRole) || 'workspace-member';

  const [showAddItems, setShowAddItems] = useState(false);
  const [, setSubmittedCount] = useState(0);

  useEffect(() => {
    fetchRounds();
    fetchProposals();
    fetchItems();
    fetchAttributes();
  }, [fetchRounds, fetchProposals, fetchItems, fetchAttributes]);

  const round = rounds.find((r) => r.id === roundId);
  const roundItemList = getItemsForRound(roundId ?? '');
  const showOthers = round?.visibilityMode === 'open' || round?.status === 'revealed';

  // Group round items by itemId
  const groupedByItem = useMemo(() => {
    const map = new Map<string, typeof roundItemList>();
    roundItemList.forEach((ri) => {
      const list = map.get(ri.itemId) ?? [];
      list.push(ri);
      map.set(ri.itemId, list);
    });
    return map;
  }, [roundItemList]);

  const totalAttributes = roundItemList.length;

  // Count my submitted attributes for this round
  const mySubmittedCount = useMemo(
    () =>
      proposals.filter(
        (p) =>
          p.roundId === roundId &&
          p.proposedBy === userId &&
          p.status === 'proposed'
      ).length,
    [proposals, roundId, userId]
  );

  const handleOneSubmitted = useCallback(() => {
    setSubmittedCount((c) => c + 1);
    if (roundId && userId) {
      markParticipantSubmitted(roundId, userId);
    }
  }, [roundId, userId, markParticipantSubmitted]);

  const isComplete = mySubmittedCount >= totalAttributes && totalAttributes > 0;

  if (!round) {
    return (
      <div className="py-16 text-center text-[var(--text-ter)]">
        Round not found.{' '}
        <Link to="/prioritization/input-rounds" className="text-[var(--text-ter)] hover:underline">
          Back to rounds
        </Link>
      </div>
    );
  }

  const isBlind = round.visibilityMode === 'blind' && round.status !== 'revealed';

  return (
    <div className="max-w-2xl mx-auto space-y-6 py-6 px-4">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate(`/prioritization/input-rounds/${roundId}`)}
          className="flex items-center gap-1 text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)] mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to round
        </button>
        <div className="flex items-start gap-3">
          <div className="p-2.5 bg-[var(--slate-dim)] shrink-0">
            <Users2 className="h-5 w-5 text-[var(--text-ter)]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[var(--text-pri)]">{round.name}</h1>
            {round.description && <p className="text-sm text-[var(--text-ter)] mt-0.5">{round.description}</p>}
            <div className="flex items-center gap-3 mt-1.5 flex-wrap">
              <span className="text-xs text-[var(--text-ter)] flex items-center gap-1">
                {isBlind ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                {isBlind ? 'Blind — others\' inputs hidden' : 'Open — others\' inputs visible'}
              </span>
              {round.deadline && (
                <span className="text-xs text-[var(--text-ter)] flex items-center gap-1">
                  <CalendarDays className="h-3 w-3" />
                  Due {new Date(round.deadline).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Progress bar */}
      {totalAttributes > 0 && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium text-[var(--text-sec)]">Your progress</span>
            <span className="text-[var(--text-ter)]">{mySubmittedCount} / {totalAttributes} submitted</span>
          </div>
          <div className="w-full h-2 bg-[var(--hover-tint)] rounded-full overflow-hidden">
            <div
              className="h-full bg-[var(--slate)] rounded-full transition-all"
              style={{ width: `${totalAttributes > 0 ? Math.round((mySubmittedCount / totalAttributes) * 100) : 0}%` }}
            />
          </div>
          {isComplete && (
            <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)] flex items-center gap-1.5 font-medium">
              <CheckCircle2 className="h-4 w-4" />
              All attributes submitted!
            </p>
          )}
        </div>
      )}

      {/* Closed / revealed notice */}
      {round.status !== 'open' && (
        <div className="px-4 py-3 bg-[var(--surface)] border border-[var(--rule)] text-sm text-[var(--text-sec)]">
          {round.status === 'closed'
            ? 'This round is closed. New submissions are not accepted.'
            : 'This round has been revealed.'}
        </div>
      )}

      {/* Item groups */}
      {[...groupedByItem.entries()].map(([itemId, riList]) => {
        const item = items.find((i) => i.id === itemId);
        return (
          <div key={itemId} className="space-y-3">
            <div className="border-b border-[var(--rule)] pb-2">
              <h2 className="text-base font-semibold text-[var(--text-pri)]">{item?.title ?? itemId}</h2>
              {item?.description && (
                <p className="text-sm text-[var(--text-ter)] mt-0.5 line-clamp-2">{item.description}</p>
              )}
            </div>
            {riList.map((ri) => {
              const attr = attributes.find((a) => a.key === ri.attributeKey);
              const scale = attr?.scales?.find((s) => s.id === ri.scaleId) ?? attr?.scales?.[0] ?? null;
              return (
                <AttributeInputCard
                  key={ri.id}
                  roundId={roundId ?? ''}
                  itemId={itemId}
                  attributeKey={ri.attributeKey}
                  attributeLabel={
                    attr?.label
                      ? ri.scaleId && scale
                        ? `${attr.label} — ${scale.label}`
                        : attr.label
                      : ri.attributeKey
                  }
                  scale={scale}
                  scaleId={ri.scaleId ?? null}
                  showOthers={showOthers ?? false}
                  userId={userId}
                  userName={userName}
                  currentRole={currentRole}
                  onSubmitted={handleOneSubmitted}
                />
              );
            })}
          </div>
        );
      })}

      {/* Empty state + add items */}
      {roundItemList.length === 0 && (
        <div className="py-12 text-center bg-[var(--bg-raised)] border border-[var(--rule)]">
          <p className="text-[var(--text-ter)] text-sm">No items scoped for this round yet.</p>
          <button
            type="button"
            onClick={() => setShowAddItems(true)}
            className="mt-3 inline-flex items-center gap-1 text-sm text-[var(--text-ter)] hover:text-[var(--text-ter)]"
          >
            <Plus className="h-4 w-4" />
            Add items
          </button>
        </div>
      )}

      {/* Add items form (facilitators) */}
      {round.status === 'open' && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
          <button
            type="button"
            onClick={() => setShowAddItems((v) => !v)}
            className="flex items-center gap-1.5 text-sm text-[var(--text-ter)] hover:text-[var(--text-ter)]"
          >
            <Plus className="h-4 w-4" />
            {showAddItems ? 'Hide add-item form' : 'Add more items to this round'}
          </button>
          {showAddItems && (
            <AddItemsForm
              roundId={roundId ?? ''}
              onDone={() => { fetchRounds(); setShowAddItems(false); }}
            />
          )}
        </div>
      )}

      {/* Done button */}
      {totalAttributes > 0 && (
        <div className="flex justify-end pt-2">
          <button
            type="button"
            onClick={() => navigate(`/prioritization/input-rounds/${roundId}`)}
            className="flex items-center gap-1.5 px-5 py-2.5 bg-[var(--bg-raised)] text-white text-sm font-medium hover:bg-[var(--bg)]"
          >
            <CheckCircle2 className="h-4 w-4" />
            Done
          </button>
        </div>
      )}
    </div>
  );
}

export default InputRoundSubmit;
